import { CopyText } from '../i18n/Language';
import { useLanguage } from '../i18n/Language';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { supabase } from '../supabase';
import './NewsletterPopup.css';

const STORAGE_KEY = 'liminality_newsletter_state';

async function saveSubscriber(rawEmail, source = 'studio') {
  const clean = rawEmail.trim().toLowerCase();
  if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) return false;

  try {
    if (supabase) {
      await supabase.from('newsletter_subscribers').insert([{ email: clean, source }]);
    }
  } catch {
    // fallback to local storage when table/env is not yet connected
  }

  try {
    const existing = JSON.parse(localStorage.getItem('liminality_subscribers_local') || '[]');
    if (!existing.some(item => item.email === clean)) {
      existing.push({ email: clean, source, created_at: new Date().toISOString() });
      localStorage.setItem('liminality_subscribers_local', JSON.stringify(existing));
    }
    localStorage.setItem(STORAGE_KEY, 'minimized');
  } catch {
    // ignore storage errors
  }
  return true;
}

export function LeadCaptureBox({ light = false, source = 'studio-closing' }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const onSubmit = async e => {
    e.preventDefault();
    setStatus('submitting');
    const ok = await saveSubscriber(email, source);
    if (ok) {
      setStatus('success');
      setEmail('');
      window.dispatchEvent(new Event('liminality-subscribed'));
    } else {
      setStatus('idle');
    }
  };

  return (
    <div className={`lead-capture-box${light ? ' is-light' : ''}`}>
      <p className="lead-capture-label"><CopyText source={"Have a project in mind? Leave your email and we’ll get in touch."} /></p>
      {status === 'success' ? (
        <p className="newsletter-status" role="status"><CopyText source={"For help with your project, email info@liminalityhub.com."} /></p>
      ) : (
        <form className="newsletter-form" onSubmit={onSubmit}>
          <input
            type="email"
            className="newsletter-input"
            placeholder={t("your@email.com")}
            aria-label={t("Your email address")}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="newsletter-submit" disabled={status === 'submitting'}>
            {status === 'submitting' ? '…' : t('Contact me')}
          </button>
        </form>
      )}
    </div>
  );
}

export default function NewsletterPopup() {
  const { t } = useLanguage();
  const location = useLocation();
  const [minimized, setMinimized] = useState(false);
  const [hiddenFooter, setHiddenFooter] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'minimized') {
        setMinimized(true);
      }
    } catch {
      // default open
    }
    const onSub = () => setMinimized(true);
    window.addEventListener('liminality-subscribed', onSub);

    const onScroll = () => {
      const footer = document.querySelector('.studio-footer') || document.querySelector('footer');
      if (!footer) return;
      const rect = footer.getBoundingClientRect();
      setHiddenFooter(rect.top < window.innerHeight - 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('liminality-subscribed', onSub);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (location.pathname.replace(/^\/it(?=\/|$)/, '').startsWith('/journal')) {
    return null;
  }

  const minimize = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'minimized');
    } catch {
      // ignore storage errors
    }
    setMinimized(true);
  };

  const expand = () => {
    setStatus('idle');
    setMinimized(false);
  };

  const onSubmit = async e => {
    e.preventDefault();
    setStatus('submitting');
    const ok = await saveSubscriber(email, 'studio-popup');
    if (ok) {
      setStatus('success');
      setEmail('');
      setTimeout(() => setMinimized(true), 2400);
    } else {
      setStatus('idle');
    }
  };

  if (minimized) {
    return (
      <button
        type="button"
        className={`newsletter-pill${hiddenFooter ? ' is-hidden-footer' : ''}`}
        onClick={expand}
        aria-label={t("Open project enquiry")}
      >
        <img className="newsletter-pill-mark" src="/images/liminality-mark-alt.png" alt="" />
        <span><CopyText source={"Contact me"} /></span>
      </button>
    );
  }

  return (
    <aside className={`newsletter-popup${hiddenFooter ? ' is-hidden-footer' : ''}`} aria-label={t("Contact me")}>
      <div className="newsletter-popup-top">
        <h3><CopyText source={"Planning a project?"} /></h3>
        <button
          type="button"
          className="newsletter-close"
          onClick={minimize}
          aria-label={t("Minimize popup")}
        >
          X
        </button>
      </div>
      <p><CopyText source={"Leave your email and we’ll contact you about your project."} /></p>
      {status === 'success' ? (
        <p className="newsletter-status" role="status"><CopyText source={"For help with your project, email info@liminalityhub.com."} /></p>
      ) : (
        <form className="newsletter-form" onSubmit={onSubmit}>
          <input
            type="email"
            className="newsletter-input"
            placeholder={t("your@email.com")}
            aria-label={t("Email address")}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="newsletter-submit" disabled={status === 'submitting'}>
            {status === 'submitting' ? '…' : t('Contact me')}
          </button>
        </form>
      )}
    </aside>
  );
}
